/**
 * Application-access queries + mutations.
 *
 * Backed by internal/tenant/application.AdminHandler routes:
 *
 *   GET  /applications/access               — cross-app roster
 *   GET  /applications/{id}/access          — single-app access set
 *   PUT  /applications/{id}/access/groups   — replace the group grants
 *   PUT  /applications/{id}/access/users    — replace the user grants
 *
 * The PUT endpoints are replace-all semantics: the caller sends the
 * complete desired group_ids / user_ids array. Add/remove UX on top
 * of this is done locally in the editor — we never issue a partial
 * mutation against the wire.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  accessRosterResponseSchema,
  accessSetSchema,
  type AccessRosterResponse,
  type AccessSet,
  type ReplaceAccessGroupsInput,
  type ReplaceAccessUsersInput,
} from "./app-access-schemas";

function appsBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/applications`;
}

// ── Fetchers ────────────────────────────────────────────────────────────────

export type AccessRosterSearch = {
  offset?: number;
  limit?: number;
  // Index signature satisfies the queryKeys helper's
  // Readonly<Record<string, unknown>> parameter type without
  // widening callers' usage — new optional fields can land here
  // without touching every call-site.
  [key: string]: unknown;
};

export async function fetchAccessRoster(
  tenantID: string,
  search: AccessRosterSearch = {},
): Promise<AccessRosterResponse> {
  // Default to the server-side maximum (200) so small-to-medium
  // tenants get everything in one round-trip. Callers that need
  // explicit paging (future UI paginator) pass offset+limit.
  const raw = await apiFetch<unknown>(`${appsBase(tenantID)}/access`, {
    searchParams: {
      offset: search.offset,
      limit: search.limit ?? 200,
    },
  });
  return accessRosterResponseSchema.parse(raw);
}

export async function fetchAccessSet(
  tenantID: string,
  appId: string,
): Promise<AccessSet> {
  const raw = await apiFetch<unknown>(
    `${appsBase(tenantID)}/${encodeURIComponent(appId)}/access`,
  );
  return accessSetSchema.parse(raw);
}

// ── Queries ─────────────────────────────────────────────────────────────────

export function useAccessRosterQuery(
  tenantID: string,
  search: AccessRosterSearch = {},
) {
  return useQuery<AccessRosterResponse, ApiError>({
    queryKey: queryKeys.applications.accessRoster(tenantID, search),
    queryFn: () => fetchAccessRoster(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

export function useAccessSetQuery(tenantID: string, appId: string, enabled = true) {
  return useQuery<AccessSet, ApiError>({
    queryKey: queryKeys.applications.access(tenantID, appId),
    queryFn: () => fetchAccessSet(tenantID, appId),
    enabled: enabled && tenantID.length > 0 && appId.length > 0,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

// Both PUT endpoints return the freshly-read AccessSet so we seed
// the per-app cache from the response and skip a follow-up fetch.
// The cross-app roster is invalidated so the list view picks up
// new counts on the next render.

export function useReplaceAccessGroupsMutation(
  tenantID: string,
  appId: string,
) {
  const qc = useQueryClient();
  return useMutation<AccessSet, ApiError, ReplaceAccessGroupsInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${appsBase(tenantID)}/${encodeURIComponent(appId)}/access/groups`,
          { method: "PUT", body: input },
        ).then((raw) => accessSetSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.applications.access(tenantID, appId), updated);
      qc.invalidateQueries({
        queryKey: queryKeys.applications.accessRosterAll(tenantID),
      });
    },
  });
}

export function useReplaceAccessUsersMutation(
  tenantID: string,
  appId: string,
) {
  const qc = useQueryClient();
  return useMutation<AccessSet, ApiError, ReplaceAccessUsersInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${appsBase(tenantID)}/${encodeURIComponent(appId)}/access/users`,
          { method: "PUT", body: input },
        ).then((raw) => accessSetSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.applications.access(tenantID, appId), updated);
      qc.invalidateQueries({
        queryKey: queryKeys.applications.accessRosterAll(tenantID),
      });
    },
  });
}
